(() => {
  'use strict';

  const CODE = 'ZYVIRA10';
  const LEGACY_SELECTED_KEY = 'zyvira-selected-discount';
  const resetTimers = new WeakMap();
  let observer;

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const clearLegacySelection = () => {
    try {
      sessionStorage.removeItem(LEGACY_SELECTED_KEY);
    } catch (error) {
      // Session storage can be unavailable in strict privacy modes.
    }
  };

  const copyToClipboard = async (value) => {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (error) {
        // Continue to the compatible fallback below.
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    Object.assign(textarea.style, {
      position: 'fixed',
      inset: '0 auto auto 0',
      width: '1px',
      height: '1px',
      padding: '0',
      opacity: '0',
      pointerEvents: 'none'
    });

    document.body.appendChild(textarea);
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }

    textarea.remove();
    return copied;
  };

  const prepareCard = (card) => {
    if (!(card instanceof Element)) return;

    card.classList.remove('is-selected');

    const codeControl = card.querySelector('.zyvira-offer-code');
    const getControl = card.querySelector('.zyvira-apply-offer');
    const codeHint = codeControl?.querySelector('small');

    if (codeControl) {
      codeControl.dataset.copyDiscount = CODE;
      codeControl.setAttribute('role', 'button');
      codeControl.setAttribute('tabindex', '0');
      codeControl.setAttribute('aria-label', `Copy discount code ${CODE}`);
    }

    if (getControl) {
      getControl.dataset.copyDiscount = CODE;
      getControl.type = 'button';
      getControl.disabled = false;
      setText(getControl, `GET 10% OFF`);
    }

    setText(codeHint, 'Tap to copy');
  };

  const prepareCards = (scope = document) => {
    if (scope instanceof Element && scope.matches('.zyvira-offer-card')) {
      prepareCard(scope);
    }
    scope.querySelectorAll?.('.zyvira-offer-card').forEach(prepareCard);
  };

  const resetCard = (card) => {
    if (!card?.isConnected) return;

    card.classList.remove('is-copied', 'copy-failed', 'is-selected');
    const codeHint = card.querySelector('.zyvira-offer-code small');
    const getControl = card.querySelector('.zyvira-apply-offer');
    const status = card.querySelector('.zyvira-offer-status');

    setText(codeHint, 'Tap to copy');
    setText(getControl, `Get ${CODE}`);
    setText(status, '');
  };

  const showResult = (card, copied) => {
    if (!card) return;

    const previousTimer = resetTimers.get(card);
    if (previousTimer) window.clearTimeout(previousTimer);

    card.classList.remove('is-selected');
    card.classList.toggle('is-copied', copied);
    card.classList.toggle('copy-failed', !copied);

    const codeHint = card.querySelector('.zyvira-offer-code small');
    const getControl = card.querySelector('.zyvira-apply-offer');
    const status = card.querySelector('.zyvira-offer-status');

    if (copied) {
      setText(codeHint, 'Copied');
      setText(getControl, 'Copied!');
      setText(status, `${CODE} copied. Paste it in the discount field at checkout.`);
    } else {
      setText(codeHint, 'Copy failed');
      setText(getControl, `Copy ${CODE}`);
      setText(status, `Copy wasn’t available. Please manually copy ${CODE}.`);
    }

    resetTimers.set(card, window.setTimeout(() => resetCard(card), 2600));
  };

  const copyFromTrigger = async (trigger) => {
    const card = trigger.closest('.zyvira-offer-card');
    if (!card) return;

    const controls = card.querySelectorAll('[data-copy-discount], .zyvira-offer-code, .zyvira-apply-offer');
    controls.forEach((control) => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-busy', 'true');
    });

    const copied = await copyToClipboard(CODE);

    controls.forEach((control) => {
      if ('disabled' in control) control.disabled = false;
      control.removeAttribute('aria-busy');
    });

    showResult(card, copied);

    try {
      trigger.focus({ preventScroll: true });
    } catch (error) {
      trigger.focus?.();
    }
  };

  const handleClick = (event) => {
    const trigger = event.target.closest?.('[data-copy-discount], .zyvira-offer-code, .zyvira-apply-offer');
    if (!trigger || !trigger.closest('.zyvira-offer-card')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    void copyFromTrigger(trigger);
  };

  const handleKeydown = (event) => {
    const trigger = event.target.closest?.('.zyvira-offer-code[role="button"]');
    if (!trigger || !trigger.closest('.zyvira-offer-card')) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    void copyFromTrigger(trigger);
  };

  const initialize = () => {
    clearLegacySelection();
    prepareCards();

    /* Capture phase intentionally runs before the legacy redirect handler. */
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeydown, true);

    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) prepareCards(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
